import React, { useEffect } from 'react';
import { useLatest } from 'ahooks';
import useChart from '@intelligence/v3/_hooks/chart/useChart';
import { LANGUAGE_EN } from '@home/_config/config';
import { getHorizontalBarChartDynamicOption } from './utils';
import { HORIZONTAL_BAR_CHART_STATIC_OPTION } from './constants';

/**
 * 横向柱状图组件
 */
const HorizontalBarChart: React.FC<IHorizontalBarChartProps> = (props) => {
  const {
    locale = LANGUAGE_EN,
    loading = true,
    data,
    metricName,
    highlightName,
    showLabel = true,
    sortByValue = true,
    xAxisLabelFormatter,
    yAxisLabelFormatter,
    valueLabelFormatter,
    nameFormatter,
    tooltipFormatter,
    onBarClick,
    updateDeps,
  } = props;

  // 使用 useLatest 保持最新引用
  const xAxisLabelFormatterLatest = useLatest(xAxisLabelFormatter);
  const yAxisLabelFormatterLatest = useLatest(yAxisLabelFormatter);
  const valueLabelFormatterLatest = useLatest(valueLabelFormatter);
  const nameFormatterLatest = useLatest(nameFormatter);
  const tooltipFormatterLatest = useLatest(tooltipFormatter);

  // 初始化 chart
  const { chartNode, chartInstance, option, setOption } = useChart({
    locale: locale as string,
    initialOption: HORIZONTAL_BAR_CHART_STATIC_OPTION,
  });

  /**
   * 更新图表配置
   */
  const updateHorizontalBarChartOption = (chartData?: IHorizontalBarChartData[] | null) => {
    if (!chartInstance.current || chartInstance.current.isDisposed()) return;
    if (!chartData || chartData.length === 0) {
      setOption({
        ...option,
        yAxis: { ...option?.yAxis, data: [] } as any,
        series: [],
      });
      return;
    }

    // 生成动态配置
    const dynamicOption = getHorizontalBarChartDynamicOption({
      data: chartData,
      metricName,
      highlightName,
      showLabel,
      sortByValue,
      xAxisLabelFormatter: xAxisLabelFormatterLatest.current,
      yAxisLabelFormatter: yAxisLabelFormatterLatest.current,
      valueLabelFormatter: valueLabelFormatterLatest.current,
      nameFormatter: nameFormatterLatest.current,
      tooltipFormatter: tooltipFormatterLatest.current,
    });

    // 合并配置
    const newOption = {
      ...option,
      ...dynamicOption,
      tooltip: {
        ...option?.tooltip,
        ...dynamicOption?.tooltip,
      },
      xAxis: {
        ...option?.xAxis,
        ...dynamicOption?.xAxis,
      },
      yAxis: {
        ...option?.yAxis,
        ...dynamicOption?.yAxis,
        axisLabel: {
          // @ts-ignore
          ...option?.yAxis?.axisLabel,
          ...dynamicOption?.yAxis?.axisLabel,
        },
      },
    };

    setOption(newOption);
  };

  // 数据变化时更新图表
  useEffect(() => {
    if (loading) return;
    updateHorizontalBarChartOption(data);
  }, [locale, loading, data, metricName, highlightName, showLabel, sortByValue, updateDeps]);

  // 使用 useLatest 保持 onBarClick 最新引用
  const onBarClickLatest = useLatest(onBarClick);

  // 监听横向柱状图柱子点击事件
  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart || !onBarClickLatest.current) return;

    const handleClick = (params: any) => {
      const clickParams: IHorizontalBarChartClickParams = {
        name: params?.name || '',
        value: params?.value || 0,
        dataIndex: params?.dataIndex || 0,
        seriesIndex: params?.seriesIndex || 0,
        data: data?.[params?.dataIndex] as IHorizontalBarChartData,
      };
      onBarClickLatest.current?.(clickParams);
    };

    chart.on('click', handleClick);

    return () => {
      chart.off('click', handleClick);
    };
  }, [locale, data]);

  return (
    <div className="dbi-chart-con">
      <div className="dbi-chart-con__wrapper dbi-chart-horizontal-bar">
        <div className="dbi-chart-con__inner dbi-chart-horizontal-bar-main" ref={chartNode} />
      </div>
    </div>
  );
};

export default HorizontalBarChart;
